import React, { useEffect, useState, useContext, useRef } from 'react';
import { useHistory } from 'react-router-dom';
import { theme } from 'theme';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import Icon from '@material-ui/core/Icon';
import IconButton from '@material-ui/core/IconButton';
import Menu from '@material-ui/core/Menu';
import MenuItem from '@material-ui/core/MenuItem';
import MoreVertIcon from '@material-ui/icons/MoreVert';
import { Client } from '@stomp/stompjs';
import SockJS from 'sockjs-client';
import { isEmpty } from 'lodash';
import { getNotificationAddress } from 'utils/api';
import { useMomentWithLocale } from 'utils/hooks/useMomentWithLocale';
import { nanoid } from '@reduxjs/toolkit';
import { toast } from 'react-toastify';
import GlobalContext from 'layouts/Main/GlobalContext';
import cookies from 'js-cookie';

const styles = {
  root: {
    color: theme.themePrimary,
    background: 'none'
  },
  iconHovered: {
    color: theme.themePrimary,
    transform: 'scale(1.25,1.25)',
    transition: 'transform 0.25s ease'
  }
}

const Notification = ({ isNotificationDropdownOpen, userInfo, setHasNoRead, setLostConnect, closeDropDown }) => {
  const moment = useMomentWithLocale();
  const { t } = useTranslation();
  const history = useHistory();

  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const [isSocketConnect, setIsSocketConnect] = useState(false);
  const isMountedRef = useRef(true);
  const {
    locale,
    noticeList,
    setNoticeList,
    socketClient,
    setSocketClient
  } = useContext(GlobalContext);
  const socketClientRef = useRef(socketClient);
  socketClientRef.current = socketClient;

  const getAddressAndNotices = async () => {
    try {
      const address = await getNotificationAddress(userInfo.username)

      if (isMountedRef.current) {
        subscribeChannel(address)
      }
    } catch (err) {
      if (isMountedRef.current) {
        toast.error(err)
      }
    }
  }

  const subscribeChannel = (addressList) => {

    if (socketClient === null) return
    addressList.forEach(address => {

      socketClient.subscribe(address, function (responseBody) {
        if (!isMountedRef.current) return

        const data = JSON.parse(responseBody.body)
        const id = data.id === 0 ? nanoid() : data.id
        setNoticeList(prev => [
          { ...data, isRead: false, id  },
          ...prev
        ])
      });
    })
  }

  function socketConnect() {
    if (isSocketConnect) {
      socketClient?.deactivate();
    }
    const client = new Client({
      connectHeaders: {
        Authorization: `Bearer ${cookies.get('token')}`,
        'Accept-Language': locale
      },
      debug: () => {},
      reconnectDelay: 5000,
      heartbeatIncoming: 4000,
      heartbeatOutgoing: 4000,
      connectionTimeout: 13000
    });

    setSocketClient(client);

    client.webSocketFactory = function () {
      return new SockJS(`${window.ENV.javaRestServerUri}/websocket`);
    };

    client.beforeConnect = () => {}

    client.onConnect = (state) => {
      if (isMountedRef.current && state.command === 'CONNECTED') {
        setIsSocketConnect(true)
      }
    }

    client.onStompError = (frame) => {
      if (frame?.headers?.message?.endsWith('Invalid token.')) {
        client.deactivate()
      }
    }

    client.onWebSocketClose = (frame) => {
      if (isMountedRef.current && frame.type === 'close') {
        setIsSocketConnect(false)
      }
    }

    client.activate()
  }

  useEffect(() => {
    if (!isEmpty(userInfo) && !isSocketConnect) {
      socketConnect()
    }
  }, [userInfo, isSocketConnect])

  useEffect(() => {
    setHasNoRead(noticeList.some(notice => !notice.isRead))
  }, [noticeList])

  useEffect(() => {
    if (locale && isSocketConnect) {
      handleLanguage(locale)
    }
  }, [locale])

  useEffect(() => {
    if (isSocketConnect) {
      getAddressAndNotices()
      setLostConnect(false)
    } else {
      setLostConnect(true)
    }
  }, [isSocketConnect])

  useEffect(() => {
    return () => {
      isMountedRef.current = false
      socketClientRef.current?.deactivate()
    }
  }, [])

  const handleLanguage = (locale) => {
    socketClient.publish({ destination: `/app/notice/acceptLanguage/${locale}` })
  }

  const handleRead = (id) => {
    setNoticeList(prev => {
      const copy = [...prev]
      const item = copy.find(el => el.id === id)

      item.isRead = true

      return copy
    })

    if (socketClient !== null) {
      socketClient.publish({ destination: `/app/notice/${id}/read` })
    }
  }

  const handleReadAll = () => {
    setNoticeList(prev => {
      const copy = [...prev]
      copy.forEach(item => item.isRead = true)
      return copy
    })
    socketClient.publish({ destination: '/app/notice/readAll' })
  }

  const handleDelete = id => {
    setNoticeList(prev => prev.filter(item => item.id !== id))
    socketClient.publish({ destination: `/app/notice/${id}/delete` })
  }

  return (
    <>
      <ul className={`notification-dropdown ${isNotificationDropdownOpen ? '' : 'hidden'}`}>
        <li className="notification-head">
          {t('notification')}
          <div>
            <IconButton
              aria-controls="long-menu"
              aria-haspopup="true"
              aria-label="more"
              onClick={(e) => setAnchorEl(e.currentTarget)}
            >
              <MoreVertIcon />
            </IconButton>
            <Menu
              anchorEl={anchorEl}
              id="long-menu"
              keepMounted
              onClose={() => setAnchorEl(null)}
              open={open}
            >
              <MenuItem
                onClick={() => {
                  handleReadAll()
                  setAnchorEl(null)
                }}
              >
                {t('markAllAsRead')}
              </MenuItem>
            </Menu>
          </div>
        </li>
        {
          noticeList.length === 0 &&
        <li className="notification-none">{t('no2')}{t('enSpace')}{t('notification')}</li>
        }
        {
          noticeList.map((notice) => {
            return (
              <li
                className="notification-content"
                key={notice.id}
                onClick={() => {
                  handleRead(notice.id)
                  if (notice.link) {
                    closeDropDown()
                    history.push(notice.link)
                  }
                }}
                style={{
                  borderLeft: notice.isRead ? '3px solid transparent' : `3px solid ${theme.themePrimary}`
                }}
              >
                <div className={`${notice.isRead ? 'isRead' : ''}`}>
                  <p className="message">{notice.content}</p>
                  <p className="duration">{moment(notice.createdDate).fromNow()}</p>
                </div>
                <IconButton
                  children={<Icon>delete_outline</Icon>}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDelete(notice.id)
                  }}
                  styles={styles}
                />
              </li>
            )}
          )
        }
      </ul>
    </>
  );
};

Notification.propTypes = {
  isNotificationDropdownOpen: PropTypes.bool,
  userInfo: PropTypes.object,
  setHasNoRead: PropTypes.func,
  setLostConnect: PropTypes.func,
  closeDropDown: PropTypes.func
};

export default Notification;
